import React, { useState, useMemo, useEffect } from "react";
import { formatCurrency } from "../../CMCalculatorV2/utils/formatCurrency";
import Dropdown from "../../CMCalculatorV2/components/Dropdown";
import RevenuePacingChart from "./RevenuePacingChart";
import ShowList from "./ShowList";

const axios = require('axios').default;

const CMDashboard = ({ team, venues, csrfToken }) => {
  const [selectedVenueId, setSelectedVenueId] = useState(venues.length > 0 ? venues[0].id : null);
  const [selectedMonthIdx, setSelectedMonthIdx] = useState(0);
  const [monthOffset, setMonthOffset] = useState(0);
  const [monthData, setMonthData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!selectedVenueId || !team) return;
    if (csrfToken) axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;

    setLoading(true);
    axios.get(`/teams/${team.id}/cm/dashboard/three_month_window`, {
      params: { venue_ownership_id: selectedVenueId, month_offset: monthOffset }
    })
      .then((res) => {
        setMonthData(res.data || []);
        setLoading(false);
      })
      .catch(() => {
        setMonthData([]);
        setLoading(false);
      });
  }, [selectedVenueId, monthOffset, team && team.id]);

  const venue = useMemo(
    () => venues.find((v) => v.id === selectedVenueId) || venues[0] || {},
    [selectedVenueId, venues]
  );

  const cmPerShow = venue.shows_per_month > 0
    ? Math.round((venue.monthly_overhead || 0) / venue.shows_per_month)
    : 0;

  const now = new Date();

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between mb-4">
        <div>
          <h4 className="mb-1" style={{ fontWeight: 700 }}>
            CM Dashboard
          </h4>
          <p className="text-muted mb-0" style={{ fontSize: "0.9rem" }}>
            Monthly contribution margin scoreboard. Track forecast vs. actuals against overhead targets.
          </p>
        </div>
      </div>

      {/* Venue detail header */}
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div>
          <h5 className="mb-0" style={{ fontWeight: 700 }}>{venue.name}</h5>
          <span className="small text-muted">
            Overhead: {formatCurrency(venue.monthly_overhead || 0)}/mo
            {" | "}CM Target per show: {formatCurrency(cmPerShow)}
            {" | "}Expected shows/mo: {venue.shows_per_month || 0}
          </span>
        </div>
        <div style={{ width: 200 }}>
          <Dropdown
            label="Venue"
            value={selectedVenueId}
            options={venues.map((v) => ({ value: v.id, label: v.name }))}
            onChange={(val) => { setSelectedVenueId(parseInt(val)); setSelectedMonthIdx(0); }}
          />
        </div>
      </div>

      {loading ? (
        <div className="text-center text-muted py-5">Loading...</div>
      ) : monthData.length === 0 ? (
        <div className="text-center text-muted py-5">No data available for this venue.</div>
      ) : (
        <>
          {/* Month tabs with navigation */}
          <div className="d-flex align-items-center" style={{ gap: 8, marginBottom: 20 }}>
            <button
              onClick={() => { setMonthOffset(monthOffset - 3); setSelectedMonthIdx(0); }}
              style={{ padding: "6px 10px", border: "1px solid var(--od-c-d5d7da, #d5d7da)", borderRadius: 8, background: "var(--od-c-ffffff, #fff)", cursor: "pointer", display: "flex", alignItems: "center", boxShadow: "0px 1px 2px 0px rgba(10,13,18,0.05)" }}
              title="Previous 3 months"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M10 12L6 8L10 4" style={{ stroke: "var(--od-c-535862, #535862)" }} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            <div className="d-flex" style={{ flex: 1, backgroundColor: "var(--od-c-fafafa, #fafafa)", border: "1px solid var(--od-c-e9eaeb, #e9eaeb)", borderRadius: 10, padding: 4, gap: 4 }}>
              {monthData.map(({ month, year }, idx) => {
                const isActive = idx === selectedMonthIdx;
                const label = new Date(year, month - 1).toLocaleString("default", { month: "long", year: "numeric" });
                return (
                  <button
                    key={`${month}-${year}`}
                    onClick={() => setSelectedMonthIdx(idx)}
                    style={{ flex: 1, padding: "6px 12px", border: "none", borderRadius: 6, background: isActive ? "var(--od-c-ffffff, #fff)" : "transparent", boxShadow: isActive ? "0px 1px 3px 0px rgba(10,13,18,0.1)" : "none", cursor: "pointer", fontWeight: 600, color: isActive ? "var(--od-c-414651, #414651)" : "var(--od-c-717680, #717680)", fontSize: 14, lineHeight: "20px" }}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
            <button
              onClick={() => { setMonthOffset(monthOffset + 3); setSelectedMonthIdx(0); }}
              style={{ padding: "6px 10px", border: "1px solid var(--od-c-d5d7da, #d5d7da)", borderRadius: 8, background: "var(--od-c-ffffff, #fff)", cursor: "pointer", display: "flex", alignItems: "center", boxShadow: "0px 1px 2px 0px rgba(10,13,18,0.05)" }}
              title="Next 3 months"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M6 4L10 8L6 12" style={{ stroke: "var(--od-c-535862, #535862)" }} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>

          {/* Active month content */}
          {(() => {
            const md = monthData[selectedMonthIdx];
            if (!md) return null;
            const { month, year, shows } = md;
            const isCurrentMonth = month === (now.getMonth() + 1) && year === now.getFullYear();

            return (
              <div>
                <RevenuePacingChart
                  shows={shows}
                  venue={{ ...venue, monthlyOverhead: venue.monthly_overhead }}
                  month={month - 1}
                  year={year}
                  isCurrentMonth={isCurrentMonth}
                />
                {shows.length > 0 ? (
                  <ShowList shows={shows} cmPerShowTarget={cmPerShow} />
                ) : (
                  <div className="text-center text-muted py-5">
                    <p>No shows booked for {venue.name} in {new Date(year, month - 1).toLocaleString("default", { month: "long", year: "numeric" })}.</p>
                  </div>
                )}
              </div>
            );
          })()}
        </>
      )}
    </div>
  );
};

export default CMDashboard;
